import React from 'react';
import './MyEngagementSfasCommercialTerms.scss';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import Helpers from '../../../../utils/helper';
import { SfasTransactionFeeValues } from '../../../../slices/marketplace/timeline/models/sfas-transaction-fee.model';
import { FeeDetailsVisibilityToggle } from '../FeeDetailsVisibilityToggle/FeeDetailsVisibilityToggle';
import InfoTooltip from '../../../../components/info-tooltip/info-tooltip';
import { SfasTransactionFeeTerms } from '../../../../components/SfasTransactionFeeTerms/SfasTransactionFeeTerms';

interface MyEngagementSfasCommercialTermsProps extends Partial<SfasTransactionFeeValues> {
    monthlyEngagementFee?: number;
    startLtmRevenue: number;
    isAdminView: boolean;
    onEditEngagementValues: () => void;
    isFeeDetailsVisible?: boolean;
    onToggleFeeDetailsVisibility: (next: boolean) => void;
    transactionFeeTooltip?: string;
    monthlyEngagementFeeTooltip?: string;
}

export const MyEngagementSfasCommercialTerms = (
    props: MyEngagementSfasCommercialTermsProps
) => {
    const showFeeDetails = props.isAdminView || !!props.isFeeDetailsVisible;

    const hasAnyTransactionFeeTerm =
        props.ltmRevenueThresholdPercent != null ||
        props.feePercentOnTotalFairValue != null ||
        props.feePercentOnExcessFairValue != null;

    const hasAllTransactionFeeTerms =
        props.ltmRevenueThresholdPercent != null &&
        props.feePercentOnTotalFairValue != null &&
        props.feePercentOnExcessFairValue != null;

    const hasStartLtmRevenue = props.startLtmRevenue > 0;

    const showSentenceLayout = hasAllTransactionFeeTerms && hasStartLtmRevenue;

    const renderEditPencil = () => {
        if (!props.isAdminView) {
            return null;
        }

        return (
            <Button
                color="link"
                size="sm"
                className="p-0 ml-2"
                onClick={props.onEditEngagementValues}
                style={{ verticalAlign: 'baseline' }}>
                <FontAwesomeIcon icon={faEdit} />
            </Button>
        );
    };

    const renderPercentRow = (label: string, value: number | undefined) => (
        <p className="terms-row">
            {label}:{' '}
            <span>{value != null ? `${value.toFixed(1)}%` : '—'}</span>
        </p>
    );

    const transactionFeeHeading = (
        <>
            Transaction Fee
            {props.transactionFeeTooltip && (
                <InfoTooltip text={props.transactionFeeTooltip} idText="sfas-transaction-fee" />
            )}
        </>
    );

    const renderTransactionFeeSection = () => {
        if (!hasAnyTransactionFeeTerm) {
            return (
                <div className="transaction-fee-terms">
                    <p className="terms-row">
                        {transactionFeeHeading}: <span>—</span>
                    </p>
                </div>
            );
        }

        if (showSentenceLayout) {
            return (
                <div className="transaction-fee-terms">
                    <p className="terms-row">{transactionFeeHeading}</p>
                    <SfasTransactionFeeTerms
                        startLtmRevenue={props.startLtmRevenue}
                        ltmRevenueThresholdPercent={props.ltmRevenueThresholdPercent}
                        feePercentOnTotalFairValue={props.feePercentOnTotalFairValue}
                        feePercentOnExcessFairValue={props.feePercentOnExcessFairValue}
                    />
                </div>
            );
        }

        return (
            <div className="transaction-fee-terms">
                <p className="terms-row">{transactionFeeHeading}</p>
                {renderPercentRow(
                    'Fee % on Total Fair Value',
                    props.feePercentOnTotalFairValue
                )}
                {renderPercentRow(
                    'Fee % on Excess Fair Value',
                    props.feePercentOnExcessFairValue
                )}
            </div>
        );
    };

    return (
        <div className="commercial-terms-section sfas-commercial-terms-section">
            <div className="commercial-terms-header">
                <h6>Engagement Commercial Terms</h6>
                {props.isAdminView && (
                    <FeeDetailsVisibilityToggle
                        isFeeDetailsVisible={!!props.isFeeDetailsVisible}
                        onToggle={props.onToggleFeeDetailsVisibility}
                    />
                )}
            </div>
            {showFeeDetails && (
                <p className="terms-row">
                    Monthly Engagement Fee
                    {props.monthlyEngagementFeeTooltip && (
                        <InfoTooltip
                            text={props.monthlyEngagementFeeTooltip}
                            idText="sfas-monthly-engagement-fee"
                        />
                    )}
                    {' '}
                    <span>
                        {props.monthlyEngagementFee != null
                            ? Helpers.GetCurrencyFormat(props.monthlyEngagementFee, 0)
                            : '—'}
                    </span>
                    {renderEditPencil()}
                </p>
            )}
            {renderTransactionFeeSection()}
            <p className="engagement-agreement-note">
                Review your engagement agreement for detailed terms.
            </p>
        </div>
    );
};
